import { useMemo } from "react";
import {
  MAIN_CHAT_PRODUCT_KEY,
} from "@/types/frontend-types";
import type {
  SessionDataContextValue,
  SessionLiveContextValue,
} from "./context";
import {
  useSessionDataActions,
  useSessionDataValue,
  useSessionLiveValue,
} from "./use-session-context-values";
import type { SessionProviderActionWiring } from "./use-session-provider-action-wiring";
import type { SessionProviderStateBundle } from "./use-session-provider-state-bundle";
import type { SessionProviderLiveWiring } from "./use-session-provider-live-wiring";
import type { SessionProviderOverviewWiring } from "./use-session-provider-overview-wiring";
import type { SessionProviderRuntimeWiring } from "./use-session-provider-runtime-wiring";
import { selectAttachedFiles } from "./attached-file-index-selectors";
import { useSessionShortIdIndexes } from "./use-session-short-id-indexes";

interface SessionProviderContextWiringInput {
  sessionId: string;
  state: SessionProviderStateBundle;
  liveWiring: SessionProviderLiveWiring;
  overviewWiring: SessionProviderOverviewWiring;
  runtimeWiring: SessionProviderRuntimeWiring;
  actionWiring: SessionProviderActionWiring;
}

interface SessionProviderDataValueInput
  extends SessionProviderContextWiringInput {
  attachedFiles: SessionDataContextValue["attachedFiles"];
  shortIds: ReturnType<typeof useSessionShortIdIndexes>;
  contextActions: SessionDataContextValue["actions"];
}

export interface SessionProviderContextWiring {
  value: SessionDataContextValue;
  liveValue: SessionLiveContextValue;
}

export function useSessionProviderContextWiring(
  input: SessionProviderContextWiringInput,
): SessionProviderContextWiring {
  const products = input.state.entity.products;
  const versions = input.state.entity.versions;
  const productDetailsById = input.state.entity.productDetailsById;
  const versionDetailsById = input.state.entity.versionDetailsById;
  const collabs = input.state.conversation.collabs;
  const chatMessages = input.state.conversation.chatMessages;
  const attachedFiles = useMemo(
    () =>
      selectAttachedFiles({
        products,
        versions,
        productDetailsById,
        versionDetailsById,
        collabs,
        chatMessages,
      }),
    [
      products,
      versions,
      productDetailsById,
      versionDetailsById,
      collabs,
      chatMessages,
    ],
  );
  const shortIds = useSessionShortIdIndexes({
    products,
    versions,
    attachedFiles,
    members: input.state.entity.members,
    collabs,
    chatMessages,
    versionPreviews: input.state.entity.versionPreviews,
    collabPreviews: input.state.entity.collabPreviews,
    reflectionPreviews: input.state.entity.reflectionPreviews,
    userShortIdViews: input.state.entity.userShortIdViews,
  });
  const contextActions = useSessionProviderContextActions(input);
  const value = useSessionProviderDataValue({
    ...input,
    attachedFiles,
    shortIds,
    contextActions,
  });
  const liveValue = useSessionProviderLiveValue(input);

  return {
    value,
    liveValue,
  };
}

function useSessionProviderContextActions(
  input: SessionProviderContextWiringInput,
): SessionDataContextValue["actions"] {
  return useSessionDataActions({
    focusProduct: input.runtimeWiring.navigation.focusProduct,
    navigateToProduct: input.runtimeWiring.navigation.navigateToProduct,
    goBack: input.runtimeWiring.navigation.goBack,
    goBackN: input.runtimeWiring.navigation.goBackN,
    selectVersion: input.runtimeWiring.navigation.selectVersion,
    setChatMode: input.actionWiring.chatActions.setChatMode,
    setChatTargetVersion: input.actionWiring.chatActions.setChatTargetVersion,
    setChatTargetMessage: input.actionWiring.chatActions.setChatTargetMessage,
    setChatIndicatedMessage:
      input.actionWiring.chatActions.setChatIndicatedMessage,
    clearChatTargets: input.actionWiring.chatActions.clearChatTargets,
    markProductRead: input.actionWiring.productActions.markProductRead,
    markAllChatAsRead: input.liveWiring.readTracking.markAllChatAsRead,
    markAllCollabsAsRead: input.liveWiring.readTracking.markAllCollabsAsRead,
    markChatReadUpTo: input.liveWiring.readTracking.markChatReadUpTo,
    markCollabsReadUpTo: input.liveWiring.readTracking.markCollabsReadUpTo,
    addOwnMessageIds: input.liveWiring.readTracking.addOwnMessageIds,
    clearNewMessageIds: input.actionWiring.chatActions.clearNewMessageIds,
    loadOlderMessages: input.actionWiring.chatActions.loadOlderMessages,
    refreshProducts: input.actionWiring.productActions.refreshProducts,
    refreshChat: input.actionWiring.chatActions.refreshChat,
    refreshCollabs: input.actionWiring.chatActions.refreshCollabs,
    ensureProductVersions: input.actionWiring.productActions.ensureProductVersions,
    ensureProductCollabs: input.actionWiring.productActions.ensureProductCollabs,
    cacheProductDetail: input.liveWiring.detailCache.cacheProductDetail,
    cacheVersionDetail: input.liveWiring.detailCache.cacheVersionDetail,
    refreshMembers: input.actionWiring.refreshActions.refreshMembers,
    refreshSession: input.actionWiring.refreshActions.refreshSession,
    applySessionLocalPatch: input.overviewWiring.overview.applySessionLocalPatch,
    flagLiveFlash: input.liveWiring.liveSignals.flagLiveFlash,
    markReflectionsObserved:
      input.liveWiring.liveSignals.markReflectionsObserved,
    markProductDetailObserved:
      input.liveWiring.liveSignals.markProductDetailObserved,
    pushMemberActivity: input.liveWiring.liveSignals.pushMemberActivity,
  });
}

function useSessionProviderDataValue(
  input: SessionProviderDataValueInput,
): SessionDataContextValue {
  return useSessionDataValue({
    sessionId: input.sessionId,
    session: input.overviewWiring.overview.session,
    teamDefaultStartingRounds: input.state.meta.teamDefaultStartingRounds,
    teamDetails: input.state.meta.teamDetails,
    products: input.state.entity.products,
    versions: input.state.entity.versions,
    productDetailsById: input.state.entity.productDetailsById,
    versionDetailsById: input.state.entity.versionDetailsById,
    members: input.state.entity.members,
    attachedFiles: input.attachedFiles,
    chatMessages: input.state.conversation.chatMessages,
    collabs: input.state.conversation.collabs,
    versionPreviews: input.state.entity.versionPreviews,
    collabPreviews: input.state.entity.collabPreviews,
    reflectionPreviews: input.state.entity.reflectionPreviews,
    fromShortId: input.shortIds.fromShortId,
    toShortId: input.shortIds.toShortId,
    chatMessageIds: input.state.conversation.chatMessageIds,
    chatCursor: input.state.conversation.chatCursor,
    chatHasMore: input.state.conversation.chatHasMore,
    chatLoadingMore: input.state.conversation.chatLoadingMore,
    collabsByProduct: input.state.conversation.collabsByProduct,
    navigation: input.runtimeWiring.navigation.navigation,
    chat: input.state.interaction.chat,
    unread: input.liveWiring.readTracking.unread,
    productReads: input.liveWiring.readTracking.productReads,
    unreadCollabCounts: input.liveWiring.readTracking.unreadCollabCounts,
    productsSeen: input.liveWiring.readTracking.productsSeen,
    mainChatScopeKey: MAIN_CHAT_PRODUCT_KEY,
    isCollabUnread: input.liveWiring.readTracking.isCollabUnread,
    loading: input.state.meta.loading,
    notFound: input.state.meta.notFound,
    actions: input.contextActions,
  });
}

function useSessionProviderLiveValue(
  input: SessionProviderContextWiringInput,
): SessionLiveContextValue {
  return useSessionLiveValue({
    newMessageIds: input.state.interaction.newMessageIds,
    liveFlashes: input.liveWiring.liveSignals.liveFlashes,
    memberActivity: input.liveWiring.liveSignals.memberActivity,
  });
}
